import type { ConnectorVersionToken } from './PersistenceRef';
import { asConnectorVersionToken } from './PersistenceRef';

/**
 * Typed failures of the structure-set persistence gateway. Messages are
 * static / status-derived only — ProblemDetails `detail` may carry PHI and
 * is never read into these errors.
 */

/**
 * The server row changed since the version token was captured (409 with a
 * `currentVersion` extension). The save workflow surfaces this as a
 * conflict the user resolves by discarding-and-reloading or by an
 * acknowledged overwrite.
 */
export class StaleVersionError extends Error {
    readonly currentVersion: ConnectorVersionToken | null;

    constructor(
        currentVersion: string | null,
        /**
         * Whether an earlier attempt of the same request failed before this
         * 409 arrived. That attempt may have been applied with only its
         * response lost, so the newer version can be the user's own write
         * rather than someone else's.
         */
        readonly afterInterruptedAttempt: boolean = false
    ) {
        super('The structure set changed in the database since it was loaded');
        this.name = 'StaleVersionError';
        this.currentVersion =
            currentVersion === null
                ? null
                : asConnectorVersionToken(currentVersion);
    }
}

/**
 * A stored structure set lists a structure without its identity or ROI number.
 * Its lock, review and AI-generated state are stored under that identity, so
 * they cannot be read, and a session could not tell that the structure is
 * protected.
 */
export class StructureWithoutIdentityError extends Error {
    constructor() {
        super('A stored structure set lists a structure without its identity');
        this.name = 'StructureWithoutIdentityError';
    }
}

/** The server refused the write because the set is approved (409). */
export class ApprovedLockError extends Error {
    constructor() {
        super('The structure set is approved in the database');
        this.name = 'ApprovedLockError';
    }
}

/**
 * The request lacked its If-Match precondition (428). Always a client
 * bug — the save workflow must thread a version token into every guarded
 * call — so this fails loudly instead of mapping to a user-facing state.
 */
export class MissingPreconditionError extends Error {
    constructor() {
        super(
            'Persistence request was sent without its If-Match version token'
        );
        this.name = 'MissingPreconditionError';
    }
}

/** Any other failed request. `status` is null for transport failures. */
export class PersistenceRequestError extends Error {
    constructor(
        readonly status: number | null,
        /** The ProblemDetails `title`, when the body carried one. */
        readonly problemTitle: string | null = null,
        /**
         * Whether the response was declared `application/problem+json` — i.e.
         * whether the CONNECTOR answered, rather than something in front of it.
         * A 404 the connector declared means the row is gone; a bare 404 is a
         * reverse proxy, a misrouted request, or a build without the route, and
         * those mean opposite things for an idempotent DELETE.
         */
        readonly fromConnector: boolean = false
    ) {
        super(
            problemTitle ??
                (status === null
                    ? 'Network error while saving'
                    : `Save request failed with status ${status}`)
        );
        this.name = 'PersistenceRequestError';
    }
}
